// Intl formatting and parsing for what a user TYPES into an input (and the
// value written back into it). A number PRINTED for reading replays the
// server's format instead (utils/server-format.util.js) — Intl's CLDR data
// moves with the browser and differs from the server's for many regions.
//
// Digits are always Latin (numberingSystem "latn"), as the server prints and
// parses them: Intl ar-SA would write ١٬٢٣٤٫٥٠ and a typed "1234.50" would
// not round-trip. Grouping still follows Intl — an en-IN input reads
// 12,34,567.50 where the server prints 1,234,567.50; Intl cannot be told to
// group by three.
const LATIN_DIGITS = { numberingSystem: "latn" }

// Default locale for number formatting (BCP 47 format)
export const DEFAULT_LOCALE = "sk-SK"

// Get user locale from meta tag (source of truth)
// Could be called on connect as it should not change during the lifetime of the application
export const getUserLocale = () => {
  const metaLocale = document.querySelector('meta[name="user-locale"]')
  return metaLocale?.getAttribute("content") || DEFAULT_LOCALE
}

// Mirrors Ruby CurrencyEnums::CURRENCY_DECIMALS_OVERRIDES (docs/VAT_ROUNDING_POLICY.md §3).
// Only lists currencies whose decimal count differs from the 2-decimal default.
const CURRENCY_DECIMALS_OVERRIDES = {
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3
}
const DEFAULT_CURRENCY_DECIMALS = 2

// Currency-aware decimal count. Accepts "VND" or "vnd"; blank/unknown → default.
export const getCurrencyDecimals = (code) => {
  if (!code) return DEFAULT_CURRENCY_DECIMALS
  const key = String(code).toUpperCase()
  return CURRENCY_DECIMALS_OVERRIDES[key] ?? DEFAULT_CURRENCY_DECIMALS
}

// A caller may override one bound alone (unit prices widen only the maximum:
// BT-146 has no cap). The other bound then yields to it, or Intl.NumberFormat
// throws a RangeError on minimum > maximum.
const currencyFractionDigits = (currency, minimumOverride, maximumOverride) => {
  const decimals = getCurrencyDecimals(currency)
  const maximumFractionDigits = maximumOverride ?? Math.max(decimals, minimumOverride ?? decimals)
  const minimumFractionDigits = minimumOverride ?? Math.min(decimals, maximumFractionDigits)
  return { minimumFractionDigits, maximumFractionDigits }
}

export const formatNumber = (rawNumber, locale = DEFAULT_LOCALE, options) => {
  if (rawNumber === null) return ""

  // When style is "currency", fraction digits come from OUR currency table
  // (VND: 0, EUR: 2, BHD: 3) — the same one Ruby's currency_decimals uses —
  // never from the browser. Forcing 2 would produce phantom decimals like
  // "307 065,36 VND"; deferring to Intl is no better, because its CLDR data
  // moves with the browser: Chrome 153 formats HUF with 0 digits while we
  // store and compute HUF at 2, so 4 183,42 and 4 183,03 both rendered as
  // "4 183 HUF". See docs/VAT_ROUNDING_POLICY.md §3, §9.
  const isCurrency = options?.style === "currency"
  const defaultOptions = isCurrency
    ? currencyFractionDigits(options.currency, options.minimumFractionDigits, options.maximumFractionDigits)
    : {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }

  const mergedOptions = { ...defaultOptions, ...options, ...LATIN_DIGITS }

  // Intl.NumberFormat docs:
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#locale_options
  return new Intl.NumberFormat(locale, mergedOptions).format(
    mergedOptions.style === "percent" ? rawNumber / 100 : rawNumber
  )
}

// The group and decimal separators Intl uses for `locale`, read from a number
// large enough to be grouped: pl-PL and es-ES do not group four digits (CLDR
// minimumGroupingDigits 2), so 1234.5 has no group part at all. Cached per
// locale — every keystroke in a document form parses several inputs.
const separatorsByLocale = new Map()

const separatorsFor = (locale) => {
  if (!separatorsByLocale.has(locale)) {
    const separators = { group: "", decimal: "." }
    for (const part of new Intl.NumberFormat(locale, LATIN_DIGITS).formatToParts(1234567.5)) {
      if (part.type === "group") separators.group = part.value
      if (part.type === "decimal") separators.decimal = part.value
    }
    separatorsByLocale.set(locale, separators)
  }
  return separatorsByLocale.get(locale)
}

export const parseLocaleNumber = (stringNumber, locale = DEFAULT_LOCALE) => {
  const { group, decimal } = separatorsFor(locale)

  // Keep digits, comma, dot and minus. This also drops every space and
  // apostrophe a locale groups with — NBSP (pl-PL, en-ZA), narrow NBSP
  // (fr-FR), ’ and ' (de-CH) — and the plain space or ' a user types for
  // them, along with a currency symbol on a total.
  let normalized = String(stringNumber).replace(/[^\d.,-]/g, "")
  // Then a "." or "," group (es-ES, en-US). A literal replaceAll, not a
  // RegExp built from the separator: an empty group made the pattern "\\",
  // a SyntaxError on every parse.
  if (group) normalized = normalized.replaceAll(group, "")
  if (decimal !== ".") normalized = normalized.replaceAll(decimal, ".")

  // Convert the normalized string to a number
  const parsedNumber = parseFloat(normalized)

  return Number.isNaN(parsedNumber) ? null : parsedNumber
}

// A raw decimal as Rails renders it into an input: value="421.5", "1.0", "-1.0".
export const MACHINE_NUMBER = /^\s*-?\d+(\.\d+)?\s*$/

// The number an <input> holds. The value the SERVER rendered is Rails' raw
// decimal, never a localized one: read in de-DE or ro-RO, whose "." groups
// thousands, value="1.0" became 10 and "421.5" became 4215 — and saving the
// form stored them. So a value still equal to the input's value attribute and
// shaped like a machine number is read as one.
// Anything else (typed by the user, set by a controller, or already localized
// by documents--number-format) is in the user's locale. A Turbo snapshot
// restore keeps the localized property ("1.000") under the server's attribute
// ("1000.0"): the two differ, so it is still read with the locale.
export const parseInputNumber = (input, locale = DEFAULT_LOCALE) => {
  const value = input.value
  if (value === input.getAttribute("value") && MACHINE_NUMBER.test(value)) return Number(value)
  return parseLocaleNumber(value, locale)
}
